import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@chain-zen/ui'
import { encode } from 'base-64'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { SAVE_CLIENT_DETAILS, SAVE_STATE_PARAM } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { envConfig } from '../../../lib/env-config'
import type { AppModalProps } from '../apps-data'

/**
 * This is the modal for the EnableOAuthModal app.
 */
export function EnableOAuthModal({
  isOpen,
  onClose,
  appType,
  oAuthBaseURL,
  oAuthRedirectURISuffix,
  oAuthScope
}: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [clientID, setClientID] = useState('')
  const [oauthUrl, setOauthUrl] = useState<string | null>(null)

  useEffect(() => {
    if (oauthUrl) {
      window.location.href = oauthUrl
    }
  }, [oauthUrl])

  const context = {
    headers: {
      'ws-id': workspaceID
    }
  }

  const [saveClientDetails, { loading: saveClientLoading }] = useMutation<{ saveClientDetails: { id: string } }>(
    SAVE_CLIENT_DETAILS,
    {
      context,
      onCompleted: (data) => {
        setClientID(data.saveClientDetails.id)

        const stateParam = uuidv4()
        saveStateParam({ variables: { appType, id: stateParam } })
      },
      onError: (err) => {
        const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        setFormError(errorMessage)
        toast.error(errorMessage)
      },
      refetchQueries: [GET_APPS]
    }
  )

  const [saveStateParam, { loading: saveParamLoading }] = useMutation<{ saveStateParam: { id: string } }>(
    SAVE_STATE_PARAM,
    {
      context,
      onCompleted: (data) => {
        const state = {
          href: window.location.href,
          origin: window.location.origin,
          path: window.location.pathname,
          stateParam: data.saveStateParam.id
        }
        const stateJson = JSON.stringify(state)
        const base64State = encode(stateJson)

        const thirdPartyOAuthURL = `${oAuthBaseURL}?client_id=${clientID}&redirect_uri=${
          envConfig.oauthCallbackUrl
        }/${oAuthRedirectURISuffix}&state=${base64State}&response_type=code${oAuthScope ? `&scope=${oAuthScope}` : ''}`

        setOauthUrl(thirdPartyOAuthURL)
      },
      onError: (err) => {
        const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        setFormError(errorMessage)
        toast.error(errorMessage)
      }
    }
  )

  const handleClose = () => {
    setFormError('')
    onClose()
  }

  const handleEnable = () => {
    saveClientDetails({
      variables: { appType }
    })
  }

  const loading = saveClientLoading || saveParamLoading

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{`Enable ${appType} for Chain`}</DialogTitle>
          <DialogDescription>Enabling this integration requires a {appType} account.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-sm">
            When enabling, you will be prompted to give Chain access to install the Chain-{appType} app by
            authenticating with your {appType} login, and clicking "Allow"
          </p>

          {!!formError && <Alert variant="error" description={formError} />}

          <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
            <div className="space-x-2">
              <Button variant="outline" onClick={handleClose} type="button">
                Cancel
              </Button>
              <Button type="button" onClick={handleEnable} disabled={loading}>
                {loading ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
